import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown, Check } from "lucide-react";

/**
 * AutocompleteInput — Live-Suche mit Vorschlagsliste.
 *
 * Props:
 * - value: string
 * - onChange: (val) => void          // wird bei jedem Tippen aufgerufen
 * - onSelect: (val) => void          // wird beim Auswählen eines Vorschlags aufgerufen
 * - getSuggestions: (query) => string[]
 * - placeholder, disabled, testId, className
 */
export default function AutocompleteInput({
  value,
  onChange,
  onSelect,
  getSuggestions,
  placeholder,
  disabled = false,
  testId,
  inputClassName = "",
  emptyLabel = "Keine passenden Fahrzeuge gefunden",
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Vorschläge live berechnen
  const suggestions = getSuggestions(value || "");

  // Outside click schließt
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (val) => {
    onChange(val);
    if (onSelect) onSelect(val);
    setOpen(false);
    setHighlight(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0 && suggestions[highlight]) {
      e.preventDefault();
      handleSelect(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setHighlight(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`pr-11 ${inputClassName}`}
          data-testid={testId}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setOpen((o) => !o);
              if (!open) inputRef.current?.focus();
            }
          }}
          className="absolute right-0 inset-y-0 w-11 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
          tabIndex={-1}
          aria-label="Vorschläge öffnen"
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {suggestions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500" data-testid={testId ? `${testId}-empty` : undefined}>
              {emptyLabel}
            </div>
          ) : (
            <ul className="py-1">
              {suggestions.map((s, i) => {
                const active = i === highlight;
                const selected = s === value;
                return (
                  <li key={s}>
                    <button
                      type="button"
                      onClick={() => handleSelect(s)}
                      onMouseEnter={() => setHighlight(i)}
                      className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${
                        active ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"
                      }`}
                      data-testid={testId ? `${testId}-option-${i}` : undefined}
                    >
                      <span className="truncate">{highlightMatch(s, value)}</span>
                      {selected && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function highlightMatch(text, query) {
  const q = String(query || "").trim();
  if (!q) return text;
  const lt = text.toLowerCase();
  const lq = q.toLowerCase();
  const idx = lt.indexOf(lq);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold text-blue-700">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
}
